import type { Lab, Rgb } from './types'

/** D65 reference white, 2-degree observer. */
const WHITE_D65 = { X: 95.047, Y: 100.0, Z: 108.883 } as const

const DELTA = 6 / 29

/** Linear channels this far outside [0, 1] are reported as out of gamut rather than rounding noise. */
const GAMUT_TOLERANCE = 1e-4

export interface SrgbResult extends Rgb {
  /** Lowercase `#rrggbb`. */
  hex: string
  /** True when the Lab value has no sRGB equivalent and the channels below were clamped. */
  outOfGamut: boolean
}

/** Inverse of the CIE Lab companding function, with the linear segment below the 6/29 cutoff. */
function labInverseF(t: number): number {
  return t > DELTA ? t ** 3 : 3 * DELTA ** 2 * (t - 4 / 29)
}

/** Forward CIE Lab companding function. */
function labF(t: number): number {
  return t > DELTA ** 3 ? Math.cbrt(t) : t / (3 * DELTA ** 2) + 4 / 29
}

/** sRGB electro-optical transfer: linear light to gamma-encoded, both 0-1. */
function encodeGamma(channel: number): number {
  return channel <= 0.0031308 ? 12.92 * channel : 1.055 * channel ** (1 / 2.4) - 0.055
}

/** sRGB opto-electronic transfer: gamma-encoded to linear light, both 0-1. */
function decodeGamma(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

/**
 * Convert L*a*b* to sRGB: Lab to XYZ under D65, the linear sRGB matrix, then gamma encoding.
 *
 * Many measured dye samples sit outside the sRGB gamut, so channels are clamped and the
 * `outOfGamut` flag is set rather than silently returning a wrong-looking swatch.
 */
export function labToSrgb(lab: Lab): SrgbResult {
  const fy = (lab.L + 16) / 116
  const fx = fy + lab.a / 500
  const fz = fy - lab.b / 200

  // XYZ scaled to 0-1 so it lines up with the linear sRGB matrix below.
  const X = (WHITE_D65.X * labInverseF(fx)) / 100
  const Y = (WHITE_D65.Y * labInverseF(fy)) / 100
  const Z = (WHITE_D65.Z * labInverseF(fz)) / 100

  const linear = {
    r: 3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z,
    g: -0.969266 * X + 1.8760108 * Y + 0.041556 * Z,
    b: 0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z,
  }

  const outOfGamut = Object.values(linear).some(
    (channel) => channel < -GAMUT_TOLERANCE || channel > 1 + GAMUT_TOLERANCE,
  )

  const r = Math.round(encodeGamma(clamp01(linear.r)) * 255)
  const g = Math.round(encodeGamma(clamp01(linear.g)) * 255)
  const b = Math.round(encodeGamma(clamp01(linear.b)) * 255)

  return { r, g, b, hex: rgbToHex({ r, g, b }), outOfGamut }
}

/** Convert 8-bit sRGB back to L*a*b* under D65. */
export function srgbToLab({ r, g, b }: Rgb): Lab {
  const lr = decodeGamma(r / 255)
  const lg = decodeGamma(g / 255)
  const lb = decodeGamma(b / 255)

  const X = ((0.4124564 * lr + 0.3575761 * lg + 0.1804375 * lb) * 100) / WHITE_D65.X
  const Y = ((0.2126729 * lr + 0.7151522 * lg + 0.072175 * lb) * 100) / WHITE_D65.Y
  const Z = ((0.0193339 * lr + 0.119192 * lg + 0.9503041 * lb) * 100) / WHITE_D65.Z

  const fx = labF(X)
  const fy = labF(Y)
  const fz = labF(Z)

  return {
    L: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  }
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.min(255, Math.max(0, Math.round(value)))
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`
}

export function hexToRgb(hex: string): Rgb | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null
  const value = Number.parseInt(match[1], 16)
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 }
}

/** Convenience for the many places that only need the swatch colour. */
export function labToHex(lab: Lab): string {
  return labToSrgb(lab).hex
}

/**
 * Pick black or white text for a swatch background, using the WCAG relative
 * luminance of the converted colour.
 */
export function readableTextColour(lab: Lab): '#000000' | '#ffffff' {
  const { r, g, b } = labToSrgb(lab)
  const luminance =
    0.2126 * decodeGamma(r / 255) + 0.7152 * decodeGamma(g / 255) + 0.0722 * decodeGamma(b / 255)
  return luminance > 0.4 ? '#000000' : '#ffffff'
}
